
import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    Box, Card, CardContent, Typography, Grid, Button,
    IconButton, Checkbox, FormControl, InputLabel, Select,
    MenuItem, Dialog, DialogTitle, DialogContent, DialogActions,
    List, ListItem, ListItemText, Slider, TextField, RadioGroup, FormControlLabel, Radio, Chip
} from "@mui/material";
import { X, ChevronRight, Search, MapPin, CheckCircle2 } from "lucide-react";
import { GoogleMap, useJsApiLoader, Circle, Marker } from "@react-google-maps/api";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import LocationOnIcon from "@mui/icons-material/LocationOn";
// import { GOOGLE_MAPS_API_KEY } from "../../../config/api";
// ─── Time Picker ─────────────────────────────────────────────────────────────
const TimePickerModal = ({ value, onChange, label, openUpward = false }) => {
    const [open, setOpen] = useState(false);
    const [tempHour, setTempHour] = useState("00");
    const [tempMin, setTempMin] = useState("00");
    const [coords, setCoords] = useState({ top: 0, left: 0 });
    const btnRef = useRef(null);



    useEffect(() => {
        if (value) {
            const [h, m] = value.split(":");
            setTempHour(h); setTempMin(m);
        }
    }, [value]);

    const handleOpen = () => {
        if (btnRef.current) {
            const rect = btnRef.current.getBoundingClientRect();
            setCoords(openUpward
                ? { bottom: window.innerHeight - rect.top + 8, left: rect.left, top: "auto" }
                : { top: rect.bottom + 8, left: rect.left });
        }
        setOpen(!open);
    };

    const hours = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
    const minutes = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));

    const handleHourClick = (h) => {
        setTempHour(h);
        if (tempMin !== "00") { onChange(`${h}:${tempMin}`); setOpen(false); }
    };
    const handleMinuteClick = (m) => {
        setTempMin(m); onChange(`${tempHour}:${m}`); setOpen(false);
    };

    return (
        <div style={{ position: "relative", display: "inline-block", width: "100%" }}>
            <button ref={btnRef} onClick={handleOpen} style={{
                display: "flex", alignItems: "center", gap: "8px",
                padding: "10px 14px", borderRadius: "10px",
                border: `1.5px solid ${open ? "#3b82f6" : "#e2e8f0"}`,
                backgroundColor: open ? "#eff6ff" : "#f8fafc",
                cursor: "pointer", fontSize: "13px", fontWeight: 600,
                color: value ? "#1e40af" : "#9ca3af",
                width: "94%", justifyContent: "space-between", transition: "all 0.2s"
            }}>
                <span>🕐</span>
                <span>{value || `Set ${label}`}</span>
                <span style={{ fontSize: "10px", color: "#9ca3af" }}>
                    {openUpward ? (open ? "▼" : "▲") : (open ? "▲" : "▼")}
                </span>
            </button>

            {open && (
                <>
                    <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 1299 }} />
                    <div style={{
                        position: "fixed", top: coords.top || "auto", bottom: coords.bottom || "auto",
                        left: coords.left, zIndex: 1300, backgroundColor: "#fff", borderRadius: "14px",
                        boxShadow: "0 8px 30px rgba(0,0,0,0.18)", padding: "16px", width: "200px",
                        border: "1px solid #e2e8f0"
                    }}>
                        <div style={{
                            textAlign: "center", fontSize: "32px", fontWeight: 800, color: "#1e40af",
                            backgroundColor: "#eff6ff", borderRadius: "10px", padding: "10px",
                            marginBottom: "14px", letterSpacing: "4px"
                        }}>{tempHour}:{tempMin}</div>
                        <div style={{ textAlign: "center", fontSize: "11px", color: "#9ca3af", marginBottom: "8px", fontWeight: 500 }}>
                            Select hour then minute
                        </div>
                        <div style={{ display: "flex", gap: "8px" }}>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: "11px", fontWeight: 700, color: "#6b7280", textAlign: "center", marginBottom: "6px" }}>HH</div>
                                <div style={{ height: "150px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "2px", scrollbarWidth: "thin" }}>
                                    {hours.map(h => (
                                        <button key={h} onClick={() => handleHourClick(h)} style={{
                                            padding: "6px", borderRadius: "8px", border: "none", cursor: "pointer",
                                            fontSize: "14px", fontWeight: 600,
                                            backgroundColor: tempHour === h ? "#3b82f6" : "transparent",
                                            color: tempHour === h ? "#fff" : "#374151", transition: "all 0.15s"
                                        }}>{h}</button>
                                    ))}
                                </div>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", fontSize: "24px", fontWeight: 800, color: "#d1d5db" }}>:</div>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: "11px", fontWeight: 700, color: "#6b7280", textAlign: "center", marginBottom: "6px" }}>MM</div>
                                <div style={{ height: "150px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "2px", scrollbarWidth: "thin" }}>
                                    {minutes.map(m => (
                                        <button key={m} onClick={() => handleMinuteClick(m)} style={{
                                            padding: "6px", borderRadius: "8px", border: "none", cursor: "pointer",
                                            fontSize: "14px", fontWeight: 600,
                                            backgroundColor: tempMin === m ? "#3b82f6" : "transparent",
                                            color: tempMin === m ? "#fff" : "#374151", transition: "all 0.15s"
                                        }}>{m}</button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};


// ─── Location Search Bar (uses Google Places Autocomplete) ───────────────────
const LocationSearchBar = ({ isLoaded, selectedLocations, onAddLocation, onRemoveLocation }) => {
    const [query, setQuery] = useState("");
    const [suggestions, setSuggestions] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [loading, setLoading] = useState(false);
    const autocompleteService = useRef(null);
    const geocoderService = useRef(null);
    const debounceTimer = useRef(null);
    const inputRef = useRef(null);
    const [areaRadius, setAreaRadius] = useState(1000);
    // Initialise services once Maps is loaded
    useEffect(() => {
        if (isLoaded && window.google) {
            autocompleteService.current = new window.google.maps.places.AutocompleteService();
            geocoderService.current = new window.google.maps.Geocoder();
        }
    }, [isLoaded]);

    const fetchSuggestions = useCallback((value) => {
        if (!autocompleteService.current || value.trim().length < 2) {
            setSuggestions([]); return;
        }
        setLoading(true);
        autocompleteService.current.getPlacePredictions(
            { input: value, types: ["(cities)"] },
            (predictions, status) => {
                setLoading(false);
                if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
                    setSuggestions(predictions.slice(0, 6));
                    setShowDropdown(true);
                } else {
                    setSuggestions([]);
                }
            }
        );
    }, []);

    const handleInput = (e) => {
        const val = e.target.value;
        setQuery(val);
        clearTimeout(debounceTimer.current);
        if (val.length < 2) { setSuggestions([]); setShowDropdown(false); return; }
        debounceTimer.current = setTimeout(() => fetchSuggestions(val), 300);
    };

    const handleSelect = (prediction) => {
        setShowDropdown(false);
        setQuery("");
        setSuggestions([]);

        // Geocode to get lat/lng
        geocoderService.current.geocode({ placeId: prediction.place_id }, (results, status) => {
            if (status === "OK" && results[0]) {
                const loc = results[0].geometry.location;
                const name = prediction.structured_formatting?.main_text || prediction.description;
                const fullName = prediction.description;
                const already = selectedLocations.some(l => l.placeId === prediction.place_id);
                if (!already) {
                    onAddLocation({
                        placeId: prediction.place_id,
                        name,
                        fullName,
                        lat: loc.lat(),
                        lng: loc.lng(),
                    });
                }
            }
        });
    };

    const alreadySelected = (placeId) => selectedLocations.some(l => l.placeId === placeId);

    return (
        <Box sx={{ mt: 2 }}>
            {/* Search Input */}
            <Box sx={{ position: "relative" }}>
                <Box sx={{
                    display: "flex", alignItems: "center", gap: 1,
                    border: "1.5px solid #e2e8f0", borderRadius: "12px",
                    backgroundColor: "#f8fafc", px: 1.5, py: 0.8,
                    "&:focus-within": { borderColor: "#3b82f6", backgroundColor: "#fff" },
                    transition: "all 0.2s"
                }}>
                    <Search size={16} color="#9ca3af" />
                    <input
                        ref={inputRef}
                        value={query}
                        onChange={handleInput}
                        onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
                        placeholder="Search cities or areas…"
                        style={{
                            flex: 1, border: "none", outline: "none",
                            fontSize: "13px", fontWeight: 500, color: "#1f2937",
                            backgroundColor: "transparent",
                        }}
                    />
                    {loading && (
                        <Box sx={{
                            width: 14, height: 14, border: "2px solid #3b82f6",
                            borderTopColor: "transparent", borderRadius: "50%",
                            animation: "spin 0.7s linear infinite",
                            "@keyframes spin": { to: { transform: "rotate(360deg)" } }
                        }} />
                    )}
                    {query && (
                        <IconButton size="small" onClick={() => { setQuery(""); setSuggestions([]); setShowDropdown(false); }} sx={{ p: 0.3 }}>
                            <X size={14} color="#9ca3af" />
                        </IconButton>
                    )}
                </Box>

                {/* Dropdown Suggestions */}
                {showDropdown && suggestions.length > 0 && (
                    <>
                        <Box onClick={() => setShowDropdown(false)} sx={{ position: "fixed", inset: 0, zIndex: 9 }} />
                        <Box sx={{
                            position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0,
                            backgroundColor: "#fff", border: "1.5px solid #e2e8f0",
                            borderRadius: "14px", boxShadow: "0 12px 32px rgba(0,0,0,0.14)",
                            zIndex: 10, overflow: "hidden"
                        }}>
                            {suggestions.map((pred, i) => {
                                const selected = alreadySelected(pred.place_id);
                                return (
                                    <Box
                                        key={pred.place_id}
                                        onClick={() => handleSelect(pred)}
                                        sx={{
                                            display: "flex", alignItems: "center", gap: 1.5,
                                            px: 2, py: 1.4,
                                            cursor: "pointer",
                                            backgroundColor: selected ? "#eff6ff" : "#fff",
                                            borderBottom: i < suggestions.length - 1 ? "1px solid #f3f4f6" : "none",
                                            "&:hover": { backgroundColor: selected ? "#dbeafe" : "#f8fafc" },
                                            transition: "background 0.15s"
                                        }}
                                    >
                                        <Box sx={{
                                            width: 30, height: 30, borderRadius: "8px",
                                            backgroundColor: selected ? "#dbeafe" : "#f1f5f9",
                                            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0
                                        }}>
                                            {selected
                                                ? <CheckCircle2 size={16} color="#3b82f6" />
                                                : <MapPin size={14} color="#6b7280" />
                                            }
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography sx={{ fontSize: "13px", fontWeight: 600, color: selected ? "#1e40af" : "#1f2937", lineHeight: 1.3 }}>
                                                {pred.structured_formatting?.main_text || pred.description}
                                            </Typography>
                                            <Typography sx={{ fontSize: "11px", color: "#9ca3af", lineHeight: 1.3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                {pred.structured_formatting?.secondary_text || ""}
                                            </Typography>
                                        </Box>
                                        {selected && (
                                            <Box sx={{ px: 1, py: 0.3, backgroundColor: "#3b82f6", borderRadius: "6px" }}>
                                                <Typography sx={{ fontSize: "10px", color: "#fff", fontWeight: 700 }}>Added</Typography>
                                            </Box>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    </>
                )}
            </Box>

            {/* Selected Locations List */}
            {selectedLocations.length > 0 && (
                <Box sx={{ mt: 2 }}>
                    <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.5px", mb: 1.2 }}>
                        Selected Locations ({selectedLocations.length})
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {/* ✅ SHOW ALL LOCATION RADIUS */}
                        {selectedLocations.map((loc) => (
                            <Circle
                                key={loc.placeId}
                                center={{ lat: loc.lat, lng: loc.lng }}
                                radius={loc.radius || 1000} // ✅ FIXED
                                options={{
                                    fillColor: "#3b82f6",
                                    fillOpacity: 0.15,
                                    strokeColor: "#3b82f6",
                                    strokeWeight: 2,
                                    strokeOpacity: 0.8,
                                }}
                            />
                        ))}
                    </Box>

                    {/* Mini location detail list */}
                    <Box sx={{
                        mt: 1.5, border: "1px solid #e2e8f0", borderRadius: "12px",
                        overflow: "hidden", backgroundColor: "#fafafa"
                    }}>
                        {selectedLocations.map((loc, i) => (
                            <Box key={loc.placeId} sx={{
                                display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.2,
                                borderBottom: i < selectedLocations.length - 1 ? "1px solid #f3f4f6" : "none",
                            }}>
                                <Box sx={{
                                    width: 22, height: 22, borderRadius: "50%",
                                    backgroundColor: "#3b82f6", display: "flex",
                                    alignItems: "center", justifyContent: "center", flexShrink: 0
                                }}>
                                    <Typography sx={{ fontSize: "10px", fontWeight: 800, color: "#fff" }}>{i + 1}</Typography>
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#1f2937" }}>{loc.name}</Typography>
                                    <Typography sx={{ fontSize: "10px", color: "#9ca3af" }}>{loc.fullName}</Typography>
                                </Box>
                                <Typography sx={{ fontSize: "10px", color: "#9ca3af" }}>
                                    {loc.lat.toFixed(3)}, {loc.lng.toFixed(3)}
                                </Typography>
                                <IconButton size="small" onClick={() => onRemoveLocation(loc.placeId)} sx={{ p: 0.5, color: "#d1d5db", "&:hover": { color: "#ef4444" } }}>
                                    <X size={13} />
                                </IconButton>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}
        </Box>
    );
};


// ─── Main Component ───────────────────────────────────────────────────────────
const TargetingConfig = ({
    goal,
    onImageUpload,
    images,
    onRemoveImage,
    adPreviewRef,
    goalConfig,
    onGoalConfigChange,
    formData,
    onMultiSelect,
    onNext,
    onBack,
    onInputChange,
    setShowPayment
}) => {
    const [showItemSelector, setShowItemSelector] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState("all");
    const [showMap, setShowMap] = useState(false);
    const containerRef = useRef(null);
    const [mapCenter, setMapCenter] = useState({ lat: 28.6139, lng: 77.209 });
    const [mapZoom, setMapZoom] = useState(13);
    const [radius, setRadius] = useState(1000);
    const [isSubmitted, setIsSubmitted] = useState(false);
    // Selected locations for the search feature
    const [selectedLocations, setSelectedLocations] = useState([]);
    // const [safeMedia, setSafeMedia] = useState(null);
    // Radius for the area targeting dialog
    const [areaRadius, setAreaRadius] = useState(1000);
    const [areaMapZoom, setAreaMapZoom] = useState(13);
    const [circle, setCircle] = useState(null);

    const handleAreaRadiusChange = (e, value) => {
        setAreaRadius(value);

        // ✅ ADD THIS LINE (IMPORTANT)
        onGoalConfigChange("leads", "radiusKm", value);

        setSelectedLocations(prev =>
            prev.map((loc, index) =>
                index === prev.length - 1
                    ? { ...loc, radius: value }
                    : loc
            )
        );
    };

    useEffect(() => {
        if (showMap) getCurrentLocation();
    }, [showMap]);

    const { isLoaded } = useJsApiLoader({
        // googleMapsApiKey: GOOGLE_MAPS_API_KEY,
        libraries: ["places"]
    });

    const [showAreaMap, setShowAreaMap] = useState(false);
    const [areaMapCenter, setAreaMapCenter] = useState({ lat: 28.6139, lng: 77.209 });

    const categories = [
        { id: "all", name: "All Items" },
        { id: "food", name: "Food & Beverages" },
        { id: "electronics", name: "Electronics" },
        { id: "fashion", name: "Fashion" },
        { id: "home", name: "Home & Living" },
    ];

    const menuItems = [
        { id: 1, name: "Margherita Pizza", category: "food", price: 299 },
        { id: 2, name: "Pepperoni Pizza", category: "food", price: 399 },
        { id: 3, name: "iPhone 13 Case", category: "electronics", price: 499 },
        { id: 4, name: "Wireless Earbuds", category: "electronics", price: 1999 },
        { id: 5, name: "Cotton T-Shirt", category: "fashion", price: 599 },
    ];
    const handleInterestSelect = (value) => {
        const current = formData?.goalConfig?.leads?.interests || [];

        const updated = current.includes(value)
            ? current.filter(v => v !== value)
            : [...current, value];

        onGoalConfigChange("leads", "interests", updated);
    };
    const getFilteredItems = () =>
        menuItems.filter(item => selectedCategory === "all" || item.category === selectedCategory);

    const handleItemSelect = (item) => {
        const currentItems = goalConfig.discount.selectedItems || [];
        const exists = currentItems.some(i => i.id === item.id);
        onGoalConfigChange("discount", "selectedItems",
            exists ? currentItems.filter(i => i.id !== item.id) : [...currentItems, item]);
    };

    const handleRadiusChange = (e, value) => {
        setRadius(value);
        if (value <= 500) setMapZoom(15);
        else if (value <= 1000) setMapZoom(14);
        else if (value <= 3000) setMapZoom(13);
        else if (value <= 6000) setMapZoom(12);
        else setMapZoom(11);
        onGoalConfigChange(goal === "discount" ? "discount" : "branding", "radius", value);
    };

    const getCurrentLocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(pos => {
                setMapCenter({ lat: pos.coords.latitude, lng: pos.coords.longitude });
                if (radius <= 500) setMapZoom(15);
                else if (radius <= 1000) setMapZoom(14);
                else if (radius <= 3000) setMapZoom(13);
                else setMapZoom(12);
            }, () => alert("Location access denied."));
        }
    };
    const handleGetCurrentLocation = () => {
        if (!navigator.geolocation) {
            alert("Geolocation not supported");
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                const lat = position.coords.latitude;
                const lng = position.coords.longitude;

                // 🔥 Reverse Geocoding
                const geocoder = new window.google.maps.Geocoder();

                geocoder.geocode(
                    { location: { lat, lng } },
                    (results, status) => {
                        if (status === "OK" && results[0]) {

                            let city = "";

                            results[0].address_components.forEach(component => {
                                if (
                                    component.types.includes("locality") ||
                                    component.types.includes("administrative_area_level_2")
                                ) {
                                    city = component.long_name;
                                }
                            });

                            const loc = {
                                placeId: `current-${Date.now()}`,
                                name: city || "Current Location",
                                fullName: "Your Current Location",

                                // ✅ ADD THESE
                                latitude: lat,
                                longitude: lng,

                                // (optional fallback)
                                lat,
                                lng
                            };

                            // ✅ Add marker
                            handleAddLocation(loc);

                            // ✅ Move map
                            setAreaMapCenter({ lat, lng });

                        } else {
                            alert("Geocoder failed");
                        }
                    }
                );
            },
            (error) => {
                // console.error(error);
                alert("Unable to fetch location");
            }
        );
    };
    useEffect(() => {
        // alert("Selected Locations:\n" + JSON.stringify(selectedLocations, null, 2));
    }, [selectedLocations]);

    const handleAddLocation = (loc) => {
        const newLoc = {
            ...loc,
            latitude: loc.lat || loc.latitude,
            longitude: loc.lng || loc.longitude
        };

        const current = formData?.goalConfig?.leads?.locations || [];

        const updated = [...current, newLoc];

        setSelectedLocations(updated);

        // ✅ SAME LIKE interests
        onGoalConfigChange("leads", "locations", updated);

        // alert("Locations: " + JSON.stringify(updated));
    };
    const handleRemoveLocation = (placeId) => {
        const updated = selectedLocations.filter(l => l.placeId !== placeId);
        setSelectedLocations(updated);
        if (onInputChange) onInputChange("targetLocations", updated);
    };

    const labelStyle = { fontSize: "13px", fontWeight: 600, color: "#374151", marginBottom: "6px" };

    const interestOptions = [
        { value: "ONLINE_COURSES", label: "Technology" },
        { value: "FASHION", label: "Fashion" },        // ⚠️ not in backend → avoid or map
        { value: "ENTERTAINMENT", label: "Sports" },   // mapped
        { value: "TRAVEL", label: "Travel" },
        { value: "FOOD", label: "Food" },
        { value: "HEALTH", label: "Beauty" }           // mapped
    ];

    const renderGoalForm = () => {
        switch (goal) {

            // ── LEADS ──────────────────────────────────────────────────────────
            case "leads":
                return (
                    <Box >
                        {/* <Typography variant="h6" fontWeight={700} sx={{ mb: 3, color: "#1f2937" }}>
                            📋 Lead Information
                        </Typography> */}

                        <Box sx={{ display: "flex", gap: "38px", flexWrap: "wrap" }}>
                            {/* Gender */}
                            <Box sx={{ width: "180px" }}>
                                <Typography sx={labelStyle}>Gender</Typography>
                                <FormControl fullWidth size="small">
                                    <Select
                                        displayEmpty
                                        value={goalConfig.leads?.gender || ""}
                                        onChange={e => onGoalConfigChange("leads", "gender", e.target.value)}
                                        sx={{ borderRadius: "10px", backgroundColor: "#f8fafc", fontSize: "13px" }}
                                        renderValue={val => val
                                            ? val.charAt(0).toUpperCase() + val.slice(1)
                                            : <span style={{ color: "#9ca3af" }}>Select gender</span>}
                                    >
                                        <MenuItem value="male">Male</MenuItem>
                                        <MenuItem value="female">Female</MenuItem>
                                        <MenuItem value="all">All</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>

                            {/* Age */}
                            {/* All the other radious of the particular data not founded */}
                            <Box sx={{ width: "220px" }}>
                                <Typography sx={labelStyle}>
                                    Age Range ({goalConfig.leads?.ageRange?.[0] || 18} - {goalConfig.leads?.ageRange?.[1] || 100})
                                </Typography>

                                <Slider
                                    value={formData.goalConfig.leads.ageRange}
                                    min={18}
                                    max={100}   // ✅ updated
                                    onChange={(e, val) =>
                                        onGoalConfigChange("leads", "ageRange", val)
                                    }
                                />
                            </Box>

                        </Box>

                        {/* <Box sx={{ display: "flex", gap: "12px", flexWrap: "wrap", mt: 2 }}>
                        </Box> */}

                        {/* Target Audience */}
                        <Box sx={{ mt: 3 }}>
                            <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#6b7280", mb: 1 }}>
                                Audience
                            </Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                {interestOptions.map(opt => {
                                    const selected = (formData?.goalConfig?.leads?.interests || []).includes(opt.value);

                                    return (
                                        <Chip
                                            key={opt.value}
                                            label={opt.label}
                                            onClick={() => handleInterestSelect(opt.value)}
                                            variant={selected ? "filled" : "outlined"}
                                            color={selected ? "primary" : "default"}
                                        />
                                    );
                                })}
                            </Box>
                        </Box>

                        {/* ── Area Targeting Leads Data masters of the data not founded ─────────────── */}
                        <Box sx={{ mt: 3 }}>
                            {/* <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#6b7280", mb: 1 }}>
                                Location
                            </Typography> */}
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    mb: 2
                                }}
                            >
                                <Button
                                    onClick={() => setShowAreaMap(true)}
                                    startIcon={<LocationOnIcon sx={{ fontSize: 22 }} />} // ✅ bigger icon
                                    sx={{
                                        height: "50px",                 // ✅ increased height
                                        borderRadius: "30px",           // smoother pill shape
                                        textTransform: "none",
                                        fontSize: "14px",               // ✅ bigger text
                                        fontWeight: 700,
                                        padding: "0 22px",              // ✅ more spacing
                                        background: "linear-gradient(135deg, #3b82f6, #2563eb)",
                                        color: "#fff",
                                        boxShadow: "0 4px 10px rgba(59,130,246,0.35)",

                                        "& .MuiButton-startIcon": {
                                            marginRight: "8px"
                                        },

                                        "&:hover": {
                                            background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
                                            transform: "scale(1.08)"
                                        }
                                    }}
                                >
                                    Add Location
                                </Button>
                            </Box>

                            {/* Selected chips shown on page (read-only summary) */}
                            {selectedLocations.length > 0 && (
                                <Box sx={{ mt: 1.5, display: "flex", flexWrap: "wrap", gap: 1 }}>
                                    {selectedLocations.map(loc => (
                                        <Box key={loc.placeId} sx={{
                                            display: "flex", alignItems: "center", gap: 0.8,
                                            px: 1.5, py: 0.6, backgroundColor: "#eff6ff",
                                            border: "1.5px solid #bfdbfe", borderRadius: "20px",
                                        }}>
                                            <MapPin size={12} color="#3b82f6" />
                                            <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#1e40af" }}>
                                                {loc.name}
                                            </Typography>
                                            <IconButton size="small" onClick={() => handleRemoveLocation(loc.placeId)} sx={{ p: 0.2 }}>
                                                <X size={11} color="#93c5fd" />
                                            </IconButton>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Box>

                        {/* Area Map Dialog — search bar + map + selected list all inside */}
                        <Dialog open={showAreaMap} onClose={() => setShowAreaMap(false)} fullWidth maxWidth="md"
                            PaperProps={{ sx: { borderRadius: "16px", overflow: "auto" } }}>
                            <DialogTitle sx={{
                                display: "flex", justifyContent: "space-between",
                                alignItems: "center", pb: 1.5, borderBottom: "1px solid #e5e7eb"
                            }}>
                                <Typography fontWeight={700} fontSize="16px">📍 Select Target Locations</Typography>
                                <IconButton size="small" onClick={() => setShowAreaMap(false)}><X size={18} /></IconButton>
                            </DialogTitle>

                            <DialogContent sx={{ p: 0, overflow: "visible" }}>

                                {/* ── Search Bar at top of dialog ── */}


                                {/* ── Map ── */}
                                {isLoaded ? (
                                    <GoogleMap
                                        mapContainerStyle={{ width: "100%", height: "320px", position: "relative" }}
                                        center={
                                            selectedLocations.length > 0
                                                ? {
                                                    lat: selectedLocations[selectedLocations.length - 1].lat,
                                                    lng: selectedLocations[selectedLocations.length - 1].lng,
                                                }
                                                : areaMapCenter
                                        }
                                        zoom={12}
                                        options={{
                                            zoomControl: true,
                                            streetViewControl: false,
                                            mapTypeControl: false,
                                            fullscreenControl: false,
                                        }}
                                        onClick={(e) => {
                                            const loc = {
                                                placeId: `manual-${Date.now()}`,
                                                name: `Pin ${selectedLocations.length + 1}`,
                                                fullName: `${e.latLng.lat().toFixed(4)}, ${e.latLng.lng().toFixed(4)}`,
                                                lat: e.latLng.lat(),
                                                lng: e.latLng.lng(),
                                            };
                                            handleAddLocation(loc);
                                        }}
                                    >
                                        {/* MARKERS */}
                                        {selectedLocations.map((loc, i) => (
                                            <Marker
                                                key={loc.placeId}
                                                position={{ lat: loc.lat, lng: loc.lng }}
                                                label={{
                                                    text: String(i + 1),
                                                    color: "#fff",
                                                    fontWeight: "bold",
                                                    fontSize: "12px",
                                                }}
                                            />
                                        ))}

                                        {/* RADIUS */}
                                        {selectedLocations.length > 0 && (
                                            <Circle
                                                center={{
                                                    lat: selectedLocations[selectedLocations.length - 1]?.lat,
                                                    lng: selectedLocations[selectedLocations.length - 1]?.lng,
                                                }}
                                                radius={areaRadius}
                                                onLoad={(c) => setCircle(c)}
                                                onUnmount={() => setCircle(null)}
                                                options={{
                                                    fillColor: "#3b82f6",
                                                    fillOpacity: 0.15,
                                                    strokeColor: "#3b82f6",
                                                    strokeWeight: 2,
                                                    strokeOpacity: 0.8,
                                                }}
                                            />
                                        )}

                                        {/* 🔵 CURRENT LOCATION (BOTTOM RIGHT) */}
                                        <Box
                                            onClick={handleGetCurrentLocation}
                                            sx={{
                                                position: "absolute",
                                                bottom: 20,
                                                right: 10,
                                                zIndex: 10,
                                                background: "#fff",
                                                borderRadius: "50%",
                                                width: "46px",
                                                height: "46px",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                boxShadow: "0 3px 10px rgba(0,0,0,0.25)",
                                                cursor: "pointer",
                                                transition: "all 0.2s ease",

                                                "&:hover": {
                                                    background: "#eff6ff",
                                                    transform: "scale(1.1)",
                                                }
                                            }}
                                        >
                                            <MyLocationIcon sx={{ color: "#1a73e8", fontSize: 26 }} />
                                        </Box>

                                        {/* 🗺️ MAP TYPE BUTTON (ABOVE LOCATION BUTTON) */}
                                        <Box

                                            sx={{
                                                position: "absolute",
                                                bottom: 80,   // ✅ placed above location button
                                                right: 10,
                                                zIndex: 10,
                                                background: "#fff",
                                                borderRadius: "50%",
                                                width: "44px",
                                                height: "44px",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                boxShadow: "0 3px 10px rgba(0,0,0,0.25)",
                                                cursor: "pointer",
                                                transition: "all 0.2s ease",

                                                "&:hover": {
                                                    background: "#f1f5f9",
                                                    transform: "scale(1.1)",
                                                },
                                            }}
                                        >
                                            {/* You can change icon */}
                                            <span style={{ fontSize: "18px" }}>🗺️</span>
                                        </Box>
                                    </GoogleMap>
                                ) : (
                                    <Box sx={{ width: "100%", height: "320px", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#f8fafc", marginTop: "10px" }}>
                                        <Typography sx={{ fontSize: "14px", color: "#6b7280" }}>🗺️ Loading Map…</Typography>
                                    </Box>
                                )}
                                <Box sx={{ px: 2.5, pt: 2, pb: 1.5, borderBottom: "1px solid #f3f4f6", overflow: "visible" }}>
                                    <LocationSearchBar
                                        isLoaded={isLoaded}
                                        selectedLocations={selectedLocations}
                                        onAddLocation={handleAddLocation}
                                        onRemoveLocation={handleRemoveLocation}
                                    />
                                </Box>
                                {/* ── Radius Slider below map ── */}
                                <Box sx={{ px: 3, pt: 2, pb: 1, borderTop: "1px solid #f3f4f6" }}>
                                    {/* Header row */}
                                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                                        <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#374151" }}>
                                            Adjust Radius
                                        </Typography>
                                        <Box sx={{
                                            display: "flex", alignItems: "center", gap: 0.8,
                                            backgroundColor: "#eff6ff", border: "1px solid #bfdbfe",
                                            borderRadius: "8px", px: 1.5, py: 0.4
                                        }}>
                                            <Box sx={{
                                                width: 10, height: 10, borderRadius: "50%",
                                                backgroundColor: "#3b82f6", flexShrink: 0
                                            }} />
                                            <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#1e40af" }}>
                                                {areaRadius >= 1000 ? `${(areaRadius / 1000).toFixed(1)} km` : `${areaRadius} m`}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Slider */}
                                    <Slider
                                        value={areaRadius}
                                        min={100}
                                        max={100000}   // ✅ FIXED (100 km)
                                        step={500}     // ✅ smoother movement for large range
                                        onChange={handleAreaRadiusChange}
                                        valueLabelDisplay="auto"
                                        valueLabelFormat={v => v >= 1000 ? `${(v / 1000).toFixed(1)} km` : `${v} m`}
                                        sx={{
                                            color: "#3b82f6",
                                            "& .MuiSlider-thumb": {
                                                width: 20,
                                                height: 20,
                                                backgroundColor: "#fff",
                                                border: "2px solid #3b82f6",
                                                "&:hover": { boxShadow: "0 0 0 8px rgba(59,130,246,0.16)" }
                                            },
                                            "& .MuiSlider-track": { height: 5, borderRadius: "4px" },
                                            "& .MuiSlider-rail": {
                                                height: 5,
                                                borderRadius: "4px",
                                                backgroundColor: "#e5e7eb"
                                            },
                                            "& .MuiSlider-valueLabel": {
                                                backgroundColor: "#1e40af",
                                                fontSize: "11px",
                                                borderRadius: "6px"
                                            }
                                        }}
                                    />

                                    {/* Min/max labels */}
                                    <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
                                        <Typography sx={{ fontSize: "11px", color: "#9ca3af" }}>100 m</Typography>
                                        <Typography sx={{ fontSize: "11px", color: "#9ca3af" }}>100 km</Typography>
                                    </Box>

                                    {/* Visual hint */}
                                    <Box sx={{
                                        mt: 1.5, px: 1.5, py: 1,
                                        backgroundColor: "#f0f9ff",
                                        border: "1px solid #bae6fd",
                                        borderRadius: "8px",
                                        display: "flex", alignItems: "center", gap: 1
                                    }}>
                                        <Box sx={{
                                            width: 28, height: 28, borderRadius: "50%",
                                            border: "2px solid #3b82f6",
                                            backgroundColor: "rgba(59,130,246,0.12)",
                                            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0
                                        }}>
                                            <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: "#3b82f6" }} />
                                        </Box>
                                        <Typography sx={{ fontSize: "12px", color: "#0369a1" }}>
                                            Blue circle covers <strong>{areaRadius >= 1000 ? `${(areaRadius / 1000).toFixed(1)} km` : `${areaRadius} m`}</strong> radius around each pinned location
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* ── Selected locations numbered list ── */}

                            </DialogContent>

                            <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e5e7eb" }}>
                                <Button onClick={() => setShowAreaMap(false)} sx={{
                                    borderRadius: "8px", textTransform: "none", fontSize: "13px",
                                    fontWeight: 600, color: "#6b7280", border: "1px solid #e5e7eb",
                                }}>Cancel</Button>
                                <Button variant="contained" onClick={() => setShowAreaMap(false)} sx={{
                                    borderRadius: "8px", textTransform: "none", fontSize: "13px",
                                    fontWeight: 600, backgroundColor: "#3b82f6", "&:hover": { backgroundColor: "#2563eb" }
                                }}>
                                    Done ({selectedLocations.length})
                                </Button>
                            </DialogActions>
                        </Dialog>
                    </Box>
                );

            // ── BRANDING ────────────────────────────────────────────────────────
            case "branding":
                return (

                    <Box >
                        {/* <Typography variant="h6" fontWeight={700} sx={{ mb: 3, color: "#1f2937" }}>
                            📋 Lead Information
                        </Typography> */}

                        <Box sx={{ display: "flex", gap: "38px", flexWrap: "wrap" }}>
                            {/* Gender */}
                            <Box sx={{ width: "180px" }}>
                                <Typography sx={labelStyle}>Gender</Typography>
                                <FormControl fullWidth size="small">
                                    <Select
                                        displayEmpty
                                        value={goalConfig.leads?.gender || ""}
                                        onChange={e => onGoalConfigChange("leads", "gender", e.target.value)}
                                        sx={{ borderRadius: "10px", backgroundColor: "#f8fafc", fontSize: "13px" }}
                                        renderValue={val => val
                                            ? val.charAt(0).toUpperCase() + val.slice(1)
                                            : <span style={{ color: "#9ca3af" }}>Select gender</span>}
                                    >
                                        <MenuItem value="male">Male</MenuItem>
                                        <MenuItem value="female">Female</MenuItem>
                                        <MenuItem value="all">All</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>

                            {/* Age */}
                            {/* All the other radious of the particular data not founded */}
                            <Box sx={{ width: "220px" }}>
                                <Typography sx={labelStyle}>
                                    Age Range ({goalConfig.leads?.ageRange?.[0] || 18} - {goalConfig.leads?.ageRange?.[1] || 100})
                                </Typography>

                                <Slider
                                    value={formData.goalConfig.leads.ageRange}
                                    min={18}
                                    max={100}   // ✅ updated
                                    onChange={(e, val) =>
                                        onGoalConfigChange("leads", "ageRange", val)
                                    }
                                />
                            </Box>
                            {/* <Box sx={{ width: "180px" }}>
                                <Typography sx={labelStyle}>Contact Mobile</Typography>
                                <TextField fullWidth size="small"
                                    value={goalConfig.leads?.contactMobile || ""}
                                    onChange={e => onGoalConfigChange("leads", "contactMobile", e.target.value)} />
                            </Box> */}
                        </Box>

                        {/* <Box sx={{ display: "flex", gap: "12px", flexWrap: "wrap", mt: 2 }}>
                        </Box> */}

                        {/* Target Audience */}
                        <Box sx={{ mt: 3 }}>
                            <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#6b7280", mb: 1 }}>
                                Audience
                            </Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                {interestOptions.map(opt => {
                                    const selected = (formData?.goalConfig?.leads?.interests || []).includes(opt.value);

                                    return (
                                        <Chip
                                            key={opt.value}
                                            label={opt.label}
                                            onClick={() => handleInterestSelect(opt.value)}
                                            variant={selected ? "filled" : "outlined"}
                                            color={selected ? "primary" : "default"}
                                        />
                                    );
                                })}
                            </Box>
                        </Box>

                        {/* ── Area Targeting Leads Data masters of the data not founded ─────────────── */}
                        <Box sx={{ mt: 3 }}>
                            {/* <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#6b7280", mb: 1 }}>
                                Location
                            </Typography> */}
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    mb: 2
                                }}
                            >
                                <Button
                                    onClick={() => setShowAreaMap(true)}
                                    startIcon={<LocationOnIcon sx={{ fontSize: 22 }} />} // ✅ bigger icon
                                    sx={{
                                        height: "50px",                 // ✅ increased height
                                        borderRadius: "30px",           // smoother pill shape
                                        textTransform: "none",
                                        fontSize: "14px",               // ✅ bigger text
                                        fontWeight: 700,
                                        padding: "0 22px",              // ✅ more spacing
                                        background: "linear-gradient(135deg, #3b82f6, #2563eb)",
                                        color: "#fff",
                                        boxShadow: "0 4px 10px rgba(59,130,246,0.35)",

                                        "& .MuiButton-startIcon": {
                                            marginRight: "8px"
                                        },

                                        "&:hover": {
                                            background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
                                            transform: "scale(1.08)"
                                        }
                                    }}
                                >
                                    Add Location
                                </Button>
                            </Box>

                            {/* Selected chips shown on page (read-only summary) */}
                            {selectedLocations.length > 0 && (
                                <Box sx={{ mt: 1.5, display: "flex", flexWrap: "wrap", gap: 1 }}>
                                    {selectedLocations.map(loc => (
                                        <Box key={loc.placeId} sx={{
                                            display: "flex", alignItems: "center", gap: 0.8,
                                            px: 1.5, py: 0.6, backgroundColor: "#eff6ff",
                                            border: "1.5px solid #bfdbfe", borderRadius: "20px",
                                        }}>
                                            <MapPin size={12} color="#3b82f6" />
                                            <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#1e40af" }}>
                                                {loc.name}
                                            </Typography>
                                            <IconButton size="small" onClick={() => handleRemoveLocation(loc.placeId)} sx={{ p: 0.2 }}>
                                                <X size={11} color="#93c5fd" />
                                            </IconButton>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Box>

                        {/* Area Map Dialog — search bar + map + selected list all inside */}
                        <Dialog open={showAreaMap} onClose={() => setShowAreaMap(false)} fullWidth maxWidth="md"
                            PaperProps={{ sx: { borderRadius: "16px", overflow: "auto" } }}>
                            <DialogTitle sx={{
                                display: "flex", justifyContent: "space-between",
                                alignItems: "center", pb: 1.5, borderBottom: "1px solid #e5e7eb"
                            }}>
                                <Typography fontWeight={700} fontSize="16px">📍 Select Target Locations</Typography>
                                <IconButton size="small" onClick={() => setShowAreaMap(false)}><X size={18} /></IconButton>
                            </DialogTitle>

                            <DialogContent sx={{ p: 0, overflow: "visible" }}>

                                {/* ── Search Bar at top of dialog ── */}


                                {/* ── Map ── */}
                                {isLoaded ? (
                                    <GoogleMap
                                        mapContainerStyle={{ width: "100%", height: "320px", position: "relative" }}
                                        center={
                                            selectedLocations.length > 0
                                                ? {
                                                    lat: selectedLocations[selectedLocations.length - 1].lat,
                                                    lng: selectedLocations[selectedLocations.length - 1].lng,
                                                }
                                                : areaMapCenter
                                        }
                                        zoom={12}
                                        options={{
                                            zoomControl: true,
                                            streetViewControl: false,
                                            mapTypeControl: false,
                                            fullscreenControl: false,
                                        }}
                                        onClick={(e) => {
                                            const loc = {
                                                placeId: `manual-${Date.now()}`,
                                                name: `Pin ${selectedLocations.length + 1}`,
                                                fullName: `${e.latLng.lat().toFixed(4)}, ${e.latLng.lng().toFixed(4)}`,
                                                lat: e.latLng.lat(),
                                                lng: e.latLng.lng(),
                                            };
                                            handleAddLocation(loc);
                                        }}
                                    >
                                        {/* MARKERS */}
                                        {selectedLocations.map((loc, i) => (
                                            <Marker
                                                key={loc.placeId}
                                                position={{ lat: loc.lat, lng: loc.lng }}
                                                label={{
                                                    text: String(i + 1),
                                                    color: "#fff",
                                                    fontWeight: "bold",
                                                    fontSize: "12px",
                                                }}
                                            />
                                        ))}

                                        {/* RADIUS */}
                                        {selectedLocations.length > 0 && (
                                            <Circle
                                                center={{
                                                    lat: selectedLocations[selectedLocations.length - 1]?.lat,
                                                    lng: selectedLocations[selectedLocations.length - 1]?.lng,
                                                }}
                                                radius={areaRadius}
                                                onLoad={(c) => setCircle(c)}
                                                onUnmount={() => setCircle(null)}
                                                options={{
                                                    fillColor: "#3b82f6",
                                                    fillOpacity: 0.15,
                                                    strokeColor: "#3b82f6",
                                                    strokeWeight: 2,
                                                    strokeOpacity: 0.8,
                                                }}
                                            />
                                        )}

                                        {/* 🔵 CURRENT LOCATION (BOTTOM RIGHT) */}
                                        <Box
                                            onClick={handleGetCurrentLocation}
                                            sx={{
                                                position: "absolute",
                                                bottom: 20,
                                                right: 10,
                                                zIndex: 10,
                                                background: "#fff",
                                                borderRadius: "50%",
                                                width: "46px",
                                                height: "46px",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                boxShadow: "0 3px 10px rgba(0,0,0,0.25)",
                                                cursor: "pointer",
                                                transition: "all 0.2s ease",

                                                "&:hover": {
                                                    background: "#eff6ff",
                                                    transform: "scale(1.1)",
                                                }
                                            }}
                                        >
                                            <MyLocationIcon sx={{ color: "#1a73e8", fontSize: 26 }} />
                                        </Box>

                                        {/* 🗺️ MAP TYPE BUTTON (ABOVE LOCATION BUTTON) */}
                                        <Box

                                            sx={{
                                                position: "absolute",
                                                bottom: 80,   // ✅ placed above location button
                                                right: 10,
                                                zIndex: 10,
                                                background: "#fff",
                                                borderRadius: "50%",
                                                width: "44px",
                                                height: "44px",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                boxShadow: "0 3px 10px rgba(0,0,0,0.25)",
                                                cursor: "pointer",
                                                transition: "all 0.2s ease",

                                                "&:hover": {
                                                    background: "#f1f5f9",
                                                    transform: "scale(1.1)",
                                                },
                                            }}
                                        >
                                            {/* You can change icon */}
                                            <span style={{ fontSize: "18px" }}>🗺️</span>
                                        </Box>
                                    </GoogleMap>
                                ) : (
                                    <Box sx={{ width: "100%", height: "320px", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#f8fafc", marginTop: "10px" }}>
                                        <Typography sx={{ fontSize: "14px", color: "#6b7280" }}>🗺️ Loading Map…</Typography>
                                    </Box>
                                )}
                                <Box sx={{ px: 2.5, pt: 2, pb: 1.5, borderBottom: "1px solid #f3f4f6", overflow: "visible" }}>
                                    <LocationSearchBar
                                        isLoaded={isLoaded}
                                        selectedLocations={selectedLocations}
                                        onAddLocation={handleAddLocation}
                                        onRemoveLocation={handleRemoveLocation}
                                    />
                                </Box>
                                {/* ── Radius Slider below map ── */}
                                <Box sx={{ px: 3, pt: 2, pb: 1, borderTop: "1px solid #f3f4f6" }}>
                                    {/* Header row */}
                                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                                        <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#374151" }}>
                                            Adjust Radius
                                        </Typography>
                                        <Box sx={{
                                            display: "flex", alignItems: "center", gap: 0.8,
                                            backgroundColor: "#eff6ff", border: "1px solid #bfdbfe",
                                            borderRadius: "8px", px: 1.5, py: 0.4
                                        }}>
                                            <Box sx={{
                                                width: 10, height: 10, borderRadius: "50%",
                                                backgroundColor: "#3b82f6", flexShrink: 0
                                            }} />
                                            <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#1e40af" }}>
                                                {areaRadius >= 1000 ? `${(areaRadius / 1000).toFixed(1)} km` : `${areaRadius} m`}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Slider */}
                                    <Slider
                                        value={areaRadius}
                                        min={100}
                                        max={100000}   // ✅ FIXED (100 km)
                                        step={500}     // ✅ smoother movement for large range
                                        onChange={handleAreaRadiusChange}
                                        valueLabelDisplay="auto"
                                        valueLabelFormat={v => v >= 1000 ? `${(v / 1000).toFixed(1)} km` : `${v} m`}
                                        sx={{
                                            color: "#3b82f6",
                                            "& .MuiSlider-thumb": {
                                                width: 20,
                                                height: 20,
                                                backgroundColor: "#fff",
                                                border: "2px solid #3b82f6",
                                                "&:hover": { boxShadow: "0 0 0 8px rgba(59,130,246,0.16)" }
                                            },
                                            "& .MuiSlider-track": { height: 5, borderRadius: "4px" },
                                            "& .MuiSlider-rail": {
                                                height: 5,
                                                borderRadius: "4px",
                                                backgroundColor: "#e5e7eb"
                                            },
                                            "& .MuiSlider-valueLabel": {
                                                backgroundColor: "#1e40af",
                                                fontSize: "11px",
                                                borderRadius: "6px"
                                            }
                                        }}
                                    />

                                    {/* Min/max labels */}
                                    <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
                                        <Typography sx={{ fontSize: "11px", color: "#9ca3af" }}>100 m</Typography>
                                        <Typography sx={{ fontSize: "11px", color: "#9ca3af" }}>100 km</Typography>
                                    </Box>

                                    {/* Visual hint */}
                                    <Box sx={{
                                        mt: 1.5, px: 1.5, py: 1,
                                        backgroundColor: "#f0f9ff",
                                        border: "1px solid #bae6fd",
                                        borderRadius: "8px",
                                        display: "flex", alignItems: "center", gap: 1
                                    }}>
                                        <Box sx={{
                                            width: 28, height: 28, borderRadius: "50%",
                                            border: "2px solid #3b82f6",
                                            backgroundColor: "rgba(59,130,246,0.12)",
                                            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0
                                        }}>
                                            <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: "#3b82f6" }} />
                                        </Box>
                                        <Typography sx={{ fontSize: "12px", color: "#0369a1" }}>
                                            Blue circle covers <strong>{areaRadius >= 1000 ? `${(areaRadius / 1000).toFixed(1)} km` : `${areaRadius} m`}</strong> radius around each pinned location
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* ── Selected locations numbered list ── */}

                            </DialogContent>

                            <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e5e7eb" }}>
                                <Button onClick={() => setShowAreaMap(false)} sx={{
                                    borderRadius: "8px", textTransform: "none", fontSize: "13px",
                                    fontWeight: 600, color: "#6b7280", border: "1px solid #e5e7eb",
                                }}>Cancel</Button>
                                <Button variant="contained" onClick={() => setShowAreaMap(false)} sx={{
                                    borderRadius: "8px", textTransform: "none", fontSize: "13px",
                                    fontWeight: 600, backgroundColor: "#3b82f6", "&:hover": { backgroundColor: "#2563eb" }
                                }}>
                                    Done ({selectedLocations.length})
                                </Button>
                            </DialogActions>
                        </Dialog>
                    </Box>
                );

            // ── DISCOUNT ─────────────────────────────────────────────────────
            case "discount":
                return (
                    <Box>

                        {/* ✅ COUPONS */}
                        {formData.subGoal === "coupons" && (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>

                                {/* 🔹 Row 1 */}
                                <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>

                                    <Box sx={{ width: "220px" }}>
                                        <Typography sx={labelStyle}>Coupon Code</Typography>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            placeholder="Enter code"
                                            value={goalConfig.discount?.couponCode || ""}
                                            onChange={(e) =>
                                                onGoalConfigChange("discount", "couponCode", e.target.value)
                                            }
                                        />
                                    </Box>

                                    <Box sx={{ width: "200px" }}>
                                        <Typography sx={labelStyle}>Discount</Typography>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="number"
                                            placeholder="Enter amount"
                                            value={goalConfig.discount?.discountValue || ""}
                                            onChange={(e) =>
                                                onGoalConfigChange("discount", "discountValue", e.target.value)
                                            }
                                        />
                                    </Box>

                                    <Box sx={{ width: "220px" }}>
                                        <Typography sx={labelStyle}>Discount Type</Typography>
                                        <Select
                                            fullWidth
                                            size="small"
                                            value={goalConfig.discount?.discountType || ""}
                                            onChange={(e) =>
                                                onGoalConfigChange("discount", "discountType", e.target.value)
                                            }
                                            displayEmpty
                                        >
                                            <MenuItem value="">Select Type</MenuItem>
                                            <MenuItem value="PERCENTAGE">Percentage</MenuItem>
                                            <MenuItem value="FIXED_AMOUNT">Fixed Amount</MenuItem>
                                        </Select>
                                    </Box>

                                    <Box sx={{ width: "220px" }}>
                                        <Typography sx={labelStyle}>Coupon Type</Typography>
                                        <Select
                                            fullWidth
                                            size="small"
                                            value={goalConfig.discount?.couponType || ""}
                                            onChange={(e) =>
                                                onGoalConfigChange("discount", "couponType", e.target.value)
                                            }
                                            displayEmpty
                                        >
                                            <MenuItem value="">Select Type</MenuItem>
                                            <MenuItem value="FLAT">Flat</MenuItem>
                                            <MenuItem value="UPTO">Upto</MenuItem>
                                        </Select>
                                    </Box>
                                    <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>

                                        {/* Gender */}
                                        {/* <Box sx={{ width: "200px" }}>
                                            <Typography sx={labelStyle}>Gender</Typography>
                                            <FormControl fullWidth size="small">
                                                <Select
                                                    value={goalConfig.leads?.gender || ""}
                                                    onChange={(e) =>
                                                        onGoalConfigChange("leads", "gender", e.target.value)
                                                    }
                                                    displayEmpty
                                                >
                                                    <MenuItem value="">Select gender</MenuItem>
                                                    <MenuItem value="male">Male</MenuItem>
                                                    <MenuItem value="female">Female</MenuItem>
                                                    <MenuItem value="all">All</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Box> */}

                                        {/*  */}
                                        <Box sx={{ width: "200px" }}>
                                            <Typography sx={labelStyle}>
                                                Minimum Order Value
                                            </Typography>

                                            <TextField
                                                fullWidth
                                                size="small"
                                                type="number"
                                                placeholder="Enter amount"
                                                value={goalConfig.discount?.minimumOrderValue || ""}
                                                onChange={(e) =>
                                                    onGoalConfigChange(
                                                        "discount",
                                                        "minimumOrderValue",
                                                        Number(e.target.value)
                                                    )
                                                }
                                            />
                                        </Box>

                                    </Box>
                                    <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                                        {/* Start Time */}
                                        <Box sx={{ width: "230px" }}>
                                            <Typography sx={labelStyle}>Start Time</Typography>
                                            <TimePickerModal
                                                label="Start Time"
                                                value={goalConfig.discount?.startTime || ""}
                                                onChange={(val) =>
                                                    onGoalConfigChange("discount", "startTime", val)
                                                }
                                                openUpward
                                            />
                                        </Box>

                                        {/* End Time */}
                                        <Box sx={{ width: "230px" }}>
                                            <Typography sx={labelStyle}>End Time</Typography>
                                            <TimePickerModal
                                                label="End Time"
                                                value={goalConfig.discount?.endTime || ""}
                                                onChange={(val) =>
                                                    onGoalConfigChange("discount", "endTime", val)
                                                }
                                                openUpward
                                            />
                                        </Box>
                                    </Box>

                                </Box>

                                {/* 🔹 Row 2 */}


                                {/* 🔹 Location Button */}
                                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                                    <Button
                                        onClick={() => setShowAreaMap(true)}
                                        startIcon={<LocationOnIcon />}
                                        sx={{
                                            height: "45px",
                                            borderRadius: "25px",
                                            textTransform: "none",
                                            fontWeight: 600,
                                            background: "#2563eb",
                                            color: "#fff",
                                            "&:hover": { background: "#1d4ed8" }
                                        }}
                                    >
                                        Add Location
                                    </Button>
                                </Box>
                                {selectedLocations.length > 0 && (
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                        {selectedLocations.map(loc => (
                                            <Box key={loc.placeId} sx={{
                                                display: "flex", alignItems: "center", gap: 0.8,
                                                px: 1.5, py: 0.6, backgroundColor: "#eff6ff",
                                                border: "1.5px solid #bfdbfe", borderRadius: "20px",
                                            }}>
                                                <MapPin size={12} color="#3b82f6" />
                                                <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#1e40af" }}>
                                                    {loc.name}
                                                </Typography>
                                                <IconButton size="small" onClick={() => handleRemoveLocation(loc.placeId)} sx={{ p: 0.2 }}>
                                                    <X size={11} color="#93c5fd" />
                                                </IconButton>
                                            </Box>
                                        ))}
                                    </Box>
                                )}

                                {/* Area Map Dialog — search bar + map + selected list all inside */}
                                <Dialog open={showAreaMap} onClose={() => setShowAreaMap(false)} fullWidth maxWidth="md"
                                    PaperProps={{ sx: { borderRadius: "16px", overflow: "auto" } }}>
                                    <DialogTitle sx={{
                                        display: "flex", justifyContent: "space-between",
                                        alignItems: "center", pb: 1.5, borderBottom: "1px solid #e5e7eb"
                                    }}>
                                        <Typography fontWeight={700} fontSize="16px">📍 Select Target Locations</Typography>
                                        <IconButton size="small" onClick={() => setShowAreaMap(false)}><X size={18} /></IconButton>
                                    </DialogTitle>

                                    <DialogContent sx={{ p: 0, overflow: "visible" }}>

                                        {/* ── Search Bar at top of dialog ── */}


                                        {/* ── Map ── */}
                                        {isLoaded ? (
                                            <GoogleMap
                                                mapContainerStyle={{ width: "100%", height: "320px", position: "relative" }}
                                                center={
                                                    selectedLocations.length > 0
                                                        ? {
                                                            lat: selectedLocations[selectedLocations.length - 1].lat,
                                                            lng: selectedLocations[selectedLocations.length - 1].lng,
                                                        }
                                                        : areaMapCenter
                                                }
                                                zoom={12}
                                                options={{
                                                    zoomControl: true,
                                                    streetViewControl: false,
                                                    mapTypeControl: false,
                                                    fullscreenControl: false,
                                                }}
                                                onClick={(e) => {
                                                    const loc = {
                                                        placeId: `manual-${Date.now()}`,
                                                        name: `Pin ${selectedLocations.length + 1}`,
                                                        fullName: `${e.latLng.lat().toFixed(4)}, ${e.latLng.lng().toFixed(4)}`,
                                                        lat: e.latLng.lat(),
                                                        lng: e.latLng.lng(),
                                                    };
                                                    handleAddLocation(loc);
                                                }}
                                            >
                                                {/* MARKERS */}
                                                {selectedLocations.map((loc, i) => (
                                                    <Marker
                                                        key={loc.placeId}
                                                        position={{ lat: loc.lat, lng: loc.lng }}
                                                        label={{
                                                            text: String(i + 1),
                                                            color: "#fff",
                                                            fontWeight: "bold",
                                                            fontSize: "12px",
                                                        }}
                                                    />
                                                ))}

                                                {/* RADIUS */}
                                                {selectedLocations.length > 0 && (
                                                    <Circle
                                                        center={{
                                                            lat: selectedLocations[selectedLocations.length - 1]?.lat,
                                                            lng: selectedLocations[selectedLocations.length - 1]?.lng,
                                                        }}
                                                        radius={areaRadius}
                                                        onLoad={(c) => setCircle(c)}
                                                        onUnmount={() => setCircle(null)}
                                                        options={{
                                                            fillColor: "#3b82f6",
                                                            fillOpacity: 0.15,
                                                            strokeColor: "#3b82f6",
                                                            strokeWeight: 2,
                                                            strokeOpacity: 0.8,
                                                        }}
                                                    />
                                                )}

                                                {/* 🔵 CURRENT LOCATION (BOTTOM RIGHT) */}
                                                <Box
                                                    onClick={handleGetCurrentLocation}
                                                    sx={{
                                                        position: "absolute",
                                                        bottom: 20,
                                                        right: 10,
                                                        zIndex: 10,
                                                        background: "#fff",
                                                        borderRadius: "50%",
                                                        width: "46px",
                                                        height: "46px",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        boxShadow: "0 3px 10px rgba(0,0,0,0.25)",
                                                        cursor: "pointer",
                                                        transition: "all 0.2s ease",

                                                        "&:hover": {
                                                            background: "#eff6ff",
                                                            transform: "scale(1.1)",
                                                        }
                                                    }}
                                                >
                                                    <MyLocationIcon sx={{ color: "#1a73e8", fontSize: 26 }} />
                                                </Box>

                                                {/* 🗺️ MAP TYPE BUTTON (ABOVE LOCATION BUTTON) */}
                                                <Box

                                                    sx={{
                                                        position: "absolute",
                                                        bottom: 80,   // ✅ placed above location button
                                                        right: 10,
                                                        zIndex: 10,
                                                        background: "#fff",
                                                        borderRadius: "50%",
                                                        width: "44px",
                                                        height: "44px",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        boxShadow: "0 3px 10px rgba(0,0,0,0.25)",
                                                        cursor: "pointer",
                                                        transition: "all 0.2s ease",

                                                        "&:hover": {
                                                            background: "#f1f5f9",
                                                            transform: "scale(1.1)",
                                                        },
                                                    }}
                                                >
                                                    {/* You can change icon */}
                                                    <span style={{ fontSize: "18px" }}>🗺️</span>
                                                </Box>
                                            </GoogleMap>
                                        ) : (
                                            <Box sx={{ width: "100%", height: "320px", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#f8fafc", marginTop: "10px" }}>
                                                <Typography sx={{ fontSize: "14px", color: "#6b7280" }}>🗺️ Loading Map…</Typography>
                                            </Box>
                                        )}
                                        <Box sx={{ px: 2.5, pt: 2, pb: 1.5, borderBottom: "1px solid #f3f4f6", overflow: "visible" }}>
                                            <LocationSearchBar
                                                isLoaded={isLoaded}
                                                selectedLocations={selectedLocations}
                                                onAddLocation={handleAddLocation}
                                                onRemoveLocation={handleRemoveLocation}
                                            />
                                        </Box>
                                        {/* ── Radius Slider below map ── */}
                                        <Box sx={{ px: 3, pt: 2, pb: 1, borderTop: "1px solid #f3f4f6" }}>
                                            {/* Header row */}
                                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                                                <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#374151" }}>
                                                    Adjust Radius
                                                </Typography>
                                                <Box sx={{
                                                    display: "flex", alignItems: "center", gap: 0.8,
                                                    backgroundColor: "#eff6ff", border: "1px solid #bfdbfe",
                                                    borderRadius: "8px", px: 1.5, py: 0.4
                                                }}>
                                                    <Box sx={{
                                                        width: 10, height: 10, borderRadius: "50%",
                                                        backgroundColor: "#3b82f6", flexShrink: 0
                                                    }} />
                                                    <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#1e40af" }}>
                                                        {areaRadius >= 1000 ? `${(areaRadius / 1000).toFixed(1)} km` : `${areaRadius} m`}
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            {/* Slider */}
                                            <Slider
                                                value={areaRadius}
                                                min={100}
                                                max={100000}   // ✅ FIXED (100 km)
                                                step={500}     // ✅ smoother movement for large range
                                                onChange={handleAreaRadiusChange}
                                                valueLabelDisplay="auto"
                                                valueLabelFormat={v => v >= 1000 ? `${(v / 1000).toFixed(1)} km` : `${v} m`}
                                                sx={{
                                                    color: "#3b82f6",
                                                    "& .MuiSlider-thumb": {
                                                        width: 20,
                                                        height: 20,
                                                        backgroundColor: "#fff",
                                                        border: "2px solid #3b82f6",
                                                        "&:hover": { boxShadow: "0 0 0 8px rgba(59,130,246,0.16)" }
                                                    },
                                                    "& .MuiSlider-track": { height: 5, borderRadius: "4px" },
                                                    "& .MuiSlider-rail": {
                                                        height: 5,
                                                        borderRadius: "4px",
                                                        backgroundColor: "#e5e7eb"
                                                    },
                                                    "& .MuiSlider-valueLabel": {
                                                        backgroundColor: "#1e40af",
                                                        fontSize: "11px",
                                                        borderRadius: "6px"
                                                    }
                                                }}
                                            />

                                            {/* Min/max labels */}
                                            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
                                                <Typography sx={{ fontSize: "11px", color: "#9ca3af" }}>100 m</Typography>
                                                <Typography sx={{ fontSize: "11px", color: "#9ca3af" }}>100 km</Typography>
                                            </Box>

                                            {/* Visual hint */}
                                            <Box sx={{
                                                mt: 1.5, px: 1.5, py: 1,
                                                backgroundColor: "#f0f9ff",
                                                border: "1px solid #bae6fd",
                                                borderRadius: "8px",
                                                display: "flex", alignItems: "center", gap: 1
                                            }}>
                                                <Box sx={{
                                                    width: 28, height: 28, borderRadius: "50%",
                                                    border: "2px solid #3b82f6",
                                                    backgroundColor: "rgba(59,130,246,0.12)",
                                                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0
                                                }}>
                                                    <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: "#3b82f6" }} />
                                                </Box>
                                                <Typography sx={{ fontSize: "12px", color: "#0369a1" }}>
                                                    Blue circle covers <strong>{areaRadius >= 1000 ? `${(areaRadius / 1000).toFixed(1)} km` : `${areaRadius} m`}</strong> radius around each pinned location
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {/* ── Selected locations numbered list ── */}

                                    </DialogContent>

                                    <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e5e7eb" }}>
                                        <Button onClick={() => setShowAreaMap(false)} sx={{
                                            borderRadius: "8px", textTransform: "none", fontSize: "13px",
                                            fontWeight: 600, color: "#6b7280", border: "1px solid #e5e7eb",
                                        }}>Cancel</Button>
                                        <Button variant="contained" onClick={() => setShowAreaMap(false)} sx={{
                                            borderRadius: "8px", textTransform: "none", fontSize: "13px",
                                            fontWeight: 600, backgroundColor: "#3b82f6", "&:hover": { backgroundColor: "#2563eb" }
                                        }}>
                                            Done ({selectedLocations.length})
                                        </Button>
                                    </DialogActions>
                                </Dialog>
                            </Box>
                        )}

                        {/* ✅ MENU */}
                        {formData.subGoal === "menu" && (
                            <Box>
                                {/* ROW 1 - DATE + CATEGORY */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        gap: "12px",
                                        alignItems: "flex-end",
                                        mb: 2,
                                        flexWrap: "wrap"
                                    }}
                                >
                                    {/* START DATE */}
                                    <Box sx={{ width: "180px" }}>
                                        <Typography sx={labelStyle}>Start Date</Typography>
                                        <TextField
                                            type="date"
                                            size="small"
                                            fullWidth
                                            value={goalConfig.discount?.startDate || ""}
                                            onChange={(e) =>
                                                onGoalConfigChange("discount", "startDate", e.target.value)
                                            }
                                            InputLabelProps={{ shrink: true }}
                                            inputProps={{
                                                min: new Date().toISOString().split("T")[0]
                                            }}
                                        />
                                    </Box>

                                    {/* END DATE */}
                                    <Box sx={{ width: "180px" }}>
                                        <Typography sx={labelStyle}>End Date</Typography>
                                        <TextField
                                            type="date"
                                            size="small"
                                            fullWidth
                                            value={goalConfig.discount?.endDate || ""}
                                            onChange={(e) =>
                                                onGoalConfigChange("discount", "endDate", e.target.value)
                                            }
                                            InputLabelProps={{ shrink: true }}
                                            inputProps={{
                                                min:
                                                    goalConfig.discount?.startDate ||
                                                    new Date().toISOString().split("T")[0]
                                            }}
                                        />
                                    </Box>

                                    {/* TIME CATEGORY */}
                                    {/* <Box sx={{ width: "180px" }}>
                                        <Typography sx={labelStyle}>Time Category</Typography>
                                        <FormControl fullWidth size="small">
                                            <Select
                                                displayEmpty
                                                value={goalConfig.discount?.timeCategory || ""}
                                                onChange={(e) =>
                                                    onGoalConfigChange(
                                                        "discount",
                                                        "timeCategory",
                                                        e.target.value
                                                    )
                                                }
                                            >
                                                <MenuItem value="PEAK_HOURS">🔥 Peak Hours</MenuItem>
                                                <MenuItem value="RAINING_TIME">🌧️ Raining Time</MenuItem>
                                                <MenuItem value="HAPPY_HOURS">🎉 Happy Hours</MenuItem>
                                                <MenuItem value="LUNCH_TIME">🍽️ Lunch Time</MenuItem>
                                                <MenuItem value="DINNER_TIME">🌙 Dinner Time</MenuItem>
                                                <MenuItem value="EARLY_MORNING">🌅 Breakfast Time</MenuItem>
                                                <MenuItem value="LATE_NIGHT">🌃 Late Night</MenuItem>
                                                <MenuItem value="WEEKEND_SPECIAL">🎊 Weekend Special</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Box> */}
                                    <Box
                                        sx={{
                                            display: "flex",
                                            gap: "12px",
                                            alignItems: "flex-end",
                                            flexWrap: "wrap"
                                        }}
                                    >
                                        <Box sx={{ width: "180px" }}>
                                            <Typography sx={labelStyle}>Start Time</Typography>
                                            <TimePickerModal
                                                label="Start Time"
                                                value={goalConfig.discount?.startTime || ""}
                                                onChange={(val) =>
                                                    onGoalConfigChange("discount", "startTime", val)
                                                }
                                                openUpward
                                            />
                                        </Box>

                                        <Box sx={{ width: "180px" }}>
                                            <Typography sx={labelStyle}>End Time</Typography>
                                            <TimePickerModal
                                                label="End Time"
                                                value={goalConfig.discount?.endTime || ""}
                                                onChange={(val) =>
                                                    onGoalConfigChange("discount", "endTime", val)
                                                }
                                                openUpward
                                            />
                                        </Box>
                                    </Box>
                                </Box>

                                {/* ROW 2 - START TIME + END TIME */}

                            </Box>
                        )}

                    </Box>
                );
            case "BOGO_DISCOUNT":
                return (
                    <Box>
                        <Box
                            sx={{
                                display: "flex",
                                gap: "12px",
                                alignItems: "flex-end",
                                mb: 2,
                                flexWrap: "wrap"
                            }}
                        >
                            {/* START DATE */}
                            <Box sx={{ width: "180px" }}>
                                <Typography sx={labelStyle}>Start Date</Typography>
                                <TextField
                                    type="date"
                                    size="small"
                                    fullWidth
                                    value={goalConfig.discount?.startDate || ""}
                                    onChange={(e) =>
                                        onGoalConfigChange(
                                            "discount",
                                            "startDate",
                                            e.target.value
                                        )
                                    }
                                />
                            </Box>

                            {/* END DATE */}
                            <Box sx={{ width: "180px" }}>
                                <Typography sx={labelStyle}>End Date</Typography>
                                <TextField
                                    type="date"
                                    size="small"
                                    fullWidth
                                    value={goalConfig.discount?.endDate || ""}
                                    onChange={(e) =>
                                        onGoalConfigChange(
                                            "discount",
                                            "endDate",
                                            e.target.value
                                        )
                                    }
                                />
                            </Box>

                            {/* START TIME */}
                            <Box sx={{ width: "180px" }}>
                                <Typography sx={labelStyle}>Start Time</Typography>
                                <TimePickerModal
                                    label="Start Time"
                                    value={goalConfig.discount?.startTime || ""}
                                    onChange={(val) =>
                                        onGoalConfigChange(
                                            "discount",
                                            "startTime",
                                            val
                                        )
                                    }
                                />
                            </Box>

                            {/* END TIME */}
                            <Box sx={{ width: "180px" }}>
                                <Typography sx={labelStyle}>End Time</Typography>
                                <TimePickerModal
                                    label="End Time"
                                    value={goalConfig.discount?.endTime || ""}
                                    onChange={(val) =>
                                        onGoalConfigChange(
                                            "discount",
                                            "endTime",
                                            val
                                        )
                                    }
                                />
                            </Box>

                            {/* ✅ ORDER TYPE - ONLY FOR BOGO */}
                            <Box sx={{ width: "200px" }}>
                                <Typography sx={labelStyle}>Order Type</Typography>
                                <FormControl fullWidth size="small">
                                    <Select
                                        value={goalConfig.discount?.orderType || ""}
                                        onChange={(e) =>
                                            onGoalConfigChange("discount", "orderType", e.target.value)
                                        }
                                        displayEmpty
                                        sx={{ borderRadius: "10px", backgroundColor: "#f8fafc" }}
                                    >
                                        <MenuItem value="">Select Order Type</MenuItem>
                                        <MenuItem value="DINE_IN">Dine In</MenuItem>
                                        <MenuItem value="TAKEAWAY">Take Away</MenuItem>
                                        <MenuItem value="DELIVERY">Delivery</MenuItem>
                                        <MenuItem value="ALL">All</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                        </Box>
                    </Box>
                );
            case "Combos":
                return (
                    <Box>

                        {/* Row 1 - Dates */}
                        <Box
                            sx={{
                                display: "flex",
                                gap: "12px",
                                flexWrap: "wrap",
                                mb: 2
                            }}
                        >
                            <Box sx={{ width: "180px" }}>
                                <Typography sx={labelStyle}>Start Date</Typography>
                                <TextField
                                    type="date"
                                    fullWidth
                                    size="small"
                                    value={goalConfig.discount?.startDate || ""}
                                    onChange={(e) =>
                                        onGoalConfigChange(
                                            "discount",
                                            "startDate",
                                            e.target.value
                                        )
                                    }
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Box>

                            <Box sx={{ width: "180px" }}>
                                <Typography sx={labelStyle}>End Date</Typography>
                                <TextField
                                    type="date"
                                    fullWidth
                                    size="small"
                                    value={goalConfig.discount?.endDate || ""}
                                    onChange={(e) =>
                                        onGoalConfigChange(
                                            "discount",
                                            "endDate",
                                            e.target.value
                                        )
                                    }
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Box>

                            <Box sx={{ width: "180px" }}>
                                <Typography sx={labelStyle}>Start Time</Typography>

                                <TimePickerModal
                                    label="Start Time"
                                    value={goalConfig.discount?.startTime || ""}
                                    onChange={(value) =>
                                        onGoalConfigChange(
                                            "discount",
                                            "startTime",
                                            value
                                        )
                                    }
                                />
                            </Box>

                            <Box sx={{ width: "180px" }}>
                                <Typography sx={labelStyle}>End Time</Typography>

                                <TimePickerModal
                                    label="End Time"
                                    value={goalConfig.discount?.endTime || ""}
                                    onChange={(value) =>
                                        onGoalConfigChange(
                                            "discount",
                                            "endTime",
                                            value
                                        )
                                    }
                                />
                            </Box>
                        </Box>

                        {/* Row 2 */}


                    </Box>
                );
            default:
                return <Typography>Select Goal</Typography>;
        }
    };

    return (
        <Box sx={{ p: 2 }}>
            <Card elevation={3} sx={{ borderRadius: "16px", overflow: "visible", border: "1px solid #4f46e5" }}>
                <CardContent sx={{ p: { xs: 3, md: 4 }, overflow: "visible" }}>


                    {/* <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                        <Typography variant="h5" fontWeight={700}>Targeting Configuration</Typography>
                    </Box> */}


                    {renderGoalForm()}

                    {/* Back / Next */}
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",   // ✅ center both buttons
                            alignItems: "center",
                            gap: "40px",                // ✅ space between buttons
                            mt: 3
                        }}
                    >
                        <Button
                            variant="outlined"
                            size="large"
                            startIcon={<ArrowBackIcon />}
                            onClick={onBack}
                            sx={{
                                borderRadius: "10px",
                                fontWeight: 600,
                                textTransform: "none",
                                minWidth: "120px"
                            }}
                        >
                            Back
                        </Button>

                        {
                            (
                                (
                                    formData.goal === "discount" &&
                                    formData.subGoal !== "advertisement" &&
                                    formData.subGoal !== "coupons"
                                ) ||
                                formData.goal === "BOGO_DISCOUNT" ||

                                (
                                    formData.goal === "Combos" &&
                                    formData.subGoal === "offers"
                                )
                            ) ? (

                                // ✅ SHOW SUBMIT
                                <Button
                                    variant="contained"
                                    disabled={isSubmitted}
                                    onClick={() => {
                                        setIsSubmitted(true);
                                        setShowPayment(true);

                                        setTimeout(() => {
                                            if (adPreviewRef?.current) {
                                                const container = adPreviewRef.current;

                                                container.scrollTo({
                                                    top: container.scrollHeight,
                                                    behavior: "smooth"
                                                });
                                            }
                                        }, 100);
                                    }}
                                    sx={{
                                        borderRadius: "10px",
                                        textTransform: "none",
                                        fontWeight: 600,
                                        minWidth: "120px",
                                        backgroundColor: isSubmitted
                                            ? "#9ca3af"
                                            : "#f97316",
                                        color: "#fff",
                                        "&:hover": {
                                            backgroundColor: isSubmitted
                                                ? "#9ca3af"
                                                : "#ea580c"
                                        }
                                    }}
                                >
                                    {isSubmitted ? "Submitted" : "Submit"}
                                </Button>

                            ) : (

                                // ✅ SHOW NEXT
                                <Button
                                    variant="contained"
                                    endIcon={<ArrowForwardIcon />}
                                    onClick={onNext}
                                    sx={{
                                        borderRadius: "10px",
                                        fontWeight: 600,
                                        minWidth: "120px",
                                        textTransform: "none",
                                        padding: "7px"
                                    }}
                                >
                                    Next
                                </Button>

                            )
                        }
                    </Box>
                </CardContent>

            </Card>



            {/* Item Selector Dialog */}


            {/* Radius Map Dialog var */}
            <Dialog open={showMap} onClose={() => setShowMap(false)} fullWidth maxWidth="md"
                PaperProps={{ sx: { borderRadius: "16px", overflow: "auto" } }}>
                <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1, borderBottom: "1px solid #e5e7eb" }}>
                    <Typography fontWeight={700} fontSize="16px">📍 Select Area Radius</Typography>
                    <IconButton size="small" onClick={() => setShowMap(false)}><X size={18} /></IconButton>
                </DialogTitle>

                <DialogContent sx={{ p: 0, overflow: "visible" }}>

                    {/* ── Search bar inside radius dialog ── */}
                    <Box sx={{ px: 2.5, pt: 2, pb: 1.5, borderBottom: "1px solid #f3f4f6", overflow: "visible" }}>
                        <LocationSearchBar
                            isLoaded={isLoaded}
                            selectedLocations={selectedLocations}
                            onAddLocation={handleAddLocation}
                            onRemoveLocation={handleRemoveLocation}
                        />
                    </Box>

                    <Box sx={{ px: 2, pt: 2, pb: 1, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
                        <Button variant="outlined" size="small" onClick={getCurrentLocation} sx={{
                            borderRadius: "8px", textTransform: "none", fontSize: "13px", fontWeight: 600,
                            borderColor: "#3b82f6", color: "#3b82f6", "&:hover": { backgroundColor: "#eff6ff" }
                        }}>🎯 Use Current Location</Button>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, backgroundColor: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: "8px", px: 1.5, py: 0.5 }}>
                            <Typography sx={{ fontSize: "12px", color: "#6b7280" }}>Radius:</Typography>
                            <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#1e40af" }}>
                                {radius >= 1000 ? `${(radius / 1000).toFixed(1)} km` : `${radius} m`}
                            </Typography>
                        </Box>
                    </Box>

                    {isLoaded ? (
                        <GoogleMap mapContainerStyle={{ width: "100%", height: "370px" }}
                            center={mapCenter} zoom={mapZoom}
                            onClick={e => setMapCenter({ lat: e.latLng.lat(), lng: e.latLng.lng() })}
                            options={{ zoomControl: true, streetViewControl: false, mapTypeControl: false, fullscreenControl: false }}>
                            <Marker position={mapCenter} draggable
                                onDragEnd={e => setMapCenter({ lat: e.latLng.lat(), lng: e.latLng.lng() })} />
                            <Circle key={radius} center={mapCenter} radius={radius} options={{
                                fillColor: "#3b82f6", fillOpacity: 0.15,
                                strokeColor: "#3b82f6", strokeWeight: 2, strokeOpacity: 0.8
                            }} />
                        </GoogleMap>
                    ) : (
                        <Box sx={{ width: "100%", height: "370px", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#f8fafc" }}>
                            <Typography sx={{ fontSize: "14px", color: "#6b7280", fontWeight: 600 }}>🗺️ Loading Map…</Typography>
                        </Box>
                    )}

                    <Box sx={{ px: 3, pt: 2, pb: 1 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                            <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#374151" }}>Adjust Radius</Typography>
                            <Typography sx={{ fontSize: "12px", color: "#6b7280" }}>Drag slider or move marker on map</Typography>
                        </Box>
                        <Slider value={radius} min={100} max={10000} step={100} onChange={handleRadiusChange}
                            valueLabelDisplay="auto" valueLabelFormat={v => v >= 1000 ? `${(v / 1000).toFixed(1)}km` : `${v}m`}
                            sx={{
                                color: "#3b82f6",
                                "& .MuiSlider-thumb": { width: 22, height: 22, backgroundColor: "#fff", border: "2px solid #3b82f6" },
                                "& .MuiSlider-track": { height: 6, borderRadius: "4px" },
                                "& .MuiSlider-rail": { height: 6, borderRadius: "4px", backgroundColor: "#e5e7eb" },
                                "& .MuiSlider-valueLabel": { backgroundColor: "#1e40af", fontSize: "12px" }
                            }} />
                        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
                            <Typography sx={{ fontSize: "11px", color: "#9ca3af" }}>100 m</Typography>
                            <Typography sx={{ fontSize: "11px", color: "#9ca3af" }}>10 km</Typography>
                        </Box>
                        <Box sx={{ mt: 1.5, p: 1.5, backgroundColor: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "8px" }}>
                            <Typography sx={{ fontSize: "12px", color: "#6b7280" }}>
                                📍 <strong>Lat:</strong> {mapCenter.lat.toFixed(5)} &nbsp;|&nbsp;
                                <strong>Lng:</strong> {mapCenter.lng.toFixed(5)} &nbsp;|&nbsp;
                                <strong>Radius:</strong> {radius >= 1000 ? `${(radius / 1000).toFixed(1)} km` : `${radius} m`}
                            </Typography>
                        </Box>
                    </Box>
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e5e7eb", gap: 1 }}>
                    <Button onClick={() => setShowMap(false)} sx={{
                        borderRadius: "8px", textTransform: "none", fontSize: "13px", fontWeight: 600,
                        color: "#6b7280", border: "1px solid #e5e7eb", "&:hover": { backgroundColor: "#f9fafb" }
                    }}>Cancel</Button>
                    <Button variant="contained" onClick={() => {
                        onGoalConfigChange(goal === "discount" ? "discount" : "branding", "radius", radius);
                        setShowMap(false);
                    }} sx={{ borderRadius: "8px", textTransform: "none", fontSize: "13px", fontWeight: 600, backgroundColor: "#3b82f6", "&:hover": { backgroundColor: "#2563eb" } }}>
                        Save Radius
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TargetingConfig;
